import { toast } from 'sonner';
import type { ApiError } from '@/shared/services/api';

/** Type guard pentru ApiError aruncat de api.ts (vezi shared/services/api.ts). */
function isApiError(err: unknown): err is ApiError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'statusCode' in err &&
    'message' in err
  );
}

/**
 * Extrage un mesaj user-friendly dintr-un error necunoscut prins în catch().
 *
 * Prioritate:
 *   1. ApiError cu status special (network, timeout, rate limit) -> mesaj generic actionable
 *   2. ApiError.message (mesajul real backend, ex 'ANAF SPVWS2: certificat expirat')
 *   3. Error.message (fetch network error, JSON parse, etc.)
 *   4. string err (pasat direct)
 *   5. fallback param (sau 'Eroare necunoscuta')
 *
 * Caller convention: catch (err) { ... getErrorMessage(err, 'fallback') }
 * Note: catch (err) e by default 'unknown' in TS strict mode — helper-ul narrow-uiește intern.
 */
export function getErrorMessage(err: unknown, fallback?: string): string {
  if (isApiError(err)) {
    // Network-level / infrastructure
    if (err.statusCode === 0) return 'Conexiune întreruptă. Verifică internetul și reîncearcă.';
    if (err.statusCode === 504) return 'Server-ul nu a răspuns la timp. Încearcă din nou în câteva secunde.';
    if (err.statusCode === 503) return 'Serviciul e temporar indisponibil. Încearcă în câteva minute.';
    if (err.statusCode === 429) return 'Prea multe cereri într-un timp scurt. Așteaptă câteva secunde și reîncearcă.';
    // Backend message (cel mai comun caz — vine de la NestJS BadRequestException etc)
    if (err.message) return err.message;
    return fallback ?? 'Eroare necunoscută';
  }
  if (err instanceof Error) {
    return err.message || fallback || 'Eroare necunoscută';
  }
  if (typeof err === 'string') return err;
  return fallback ?? 'Eroare necunoscută';
}

/** Shortcut: extrage mesajul + apelează toast.error într-o singură chemare. */
export function notifyError(err: unknown, fallback?: string): void {
  const msg = getErrorMessage(err, fallback);
  // un mesaj lung al serverului (ex. ce trebuie făcut întâi) rămâne destul cât să poată fi citit
  toast.error(msg, msg.length > 90 ? { duration: 8000 } : undefined);
}

/**
 * Verifică dacă eroarea e legată de modul/abonament (ex: 402 MODULE_NOT_SUBSCRIBED).
 * Util când caller vrea să afișeze CTA 'upgrade plan' suplimentar.
 * Toast-ul standard pentru aceste erori e gestionat deja în api.ts — nu trebuie re-afișat.
 */
export function isModuleNotSubscribedError(err: unknown): boolean {
  return isApiError(err) && err.errorCode === 'MODULE_NOT_SUBSCRIBED';
}
